import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { UserSwitch, Users, ArrowRight, X, Buildings } from "@phosphor-icons/react";

/**
 * Phase 4+ — Admin widget for transferring "collée" prospects (Intéressé /
 * Rappel / RDV / Site web en cours) from one télépro to another.
 *
 * Typical use case : télépro en absence longue durée → transférer ses
 * leads chauds à un autre plutôt que de les laisser refroidir.
 *
 * Two modes :
 *  - Bulk : "Transférer tous les prospects de X vers Y" (1 clic)
 *  - Individuel : pick-and-choose dans la liste
 */
export default function ProspectHandoffPanel() {
  const [telepros, setTelepros] = useState([]);
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [owned, setOwned] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    api.get("/admin/telepro")
      .then((r) => setTelepros((r.data.items || []).filter((t) => t.active)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!fromId) { setOwned({ items: [], count: 0 }); return; }
    api.get(`/admin/telepros/${fromId}/owned-prospects`)
      .then((r) => setOwned({ items: r.data.items || [], count: r.data.count || 0 }))
      .catch(() => setOwned({ items: [], count: 0 }));
  }, [fromId]);

  const reloadOwned = () => {
    if (!fromId) return;
    api.get(`/admin/telepros/${fromId}/owned-prospects`)
      .then((r) => setOwned({ items: r.data.items || [], count: r.data.count || 0 }));
  };

  const singleTransfer = async (biz) => {
    if (!toId) return toast.error("Choisis d'abord le télépro cible.");
    try {
      await api.post(`/admin/businesses/${biz.id}/reassign-owner`, {
        new_telepro_id: toId,
        reason: reason || undefined,
      });
      toast.success(`✅ « ${biz.name} » transféré`);
      reloadOwned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfert impossible");
    }
  };

  const bulkTransfer = async () => {
    if (!fromId || !toId) return toast.error("Sélectionne source ET cible.");
    if (fromId === toId) return toast.error("Source et cible doivent différer.");
    if (owned.count === 0) return toast.error("Aucun prospect à transférer.");
    const from = telepros.find((t) => t.id === fromId)?.code || "?";
    const to = telepros.find((t) => t.id === toId)?.code || "?";
    if (!window.confirm(
      `Transférer les ${owned.count} prospects de ${from} vers ${to} ?\n\n` +
      `Ils apparaîtront dans la prochaine liste de ${to}. ` +
      `L'action est tracée dans l'historique CRM de chaque fiche (append-only, jamais destructif).`
    )) return;
    setBulkRunning(true);
    try {
      const r = await api.post(`/admin/telepros/${fromId}/handoff`, {
        new_telepro_id: toId,
        reason: reason || "Réassignation manuelle",
      });
      toast.success(`✅ ${r.data.transferred} prospects transférés vers ${to}`);
      reloadOwned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfert impossible");
    } finally {
      setBulkRunning(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-4">Chargement…</div>;
  }
  return (
    <div className="bg-card border border-border rounded-sm p-4 mb-6" data-testid="widget-prospect-handoff">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="label-uppercase text-primary flex items-center gap-1.5">
            <UserSwitch size={12} weight="fill"/> Handoff prospects entre télépros
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Transfère les prospects « collés » (Intéressé / Rappel / RDV / Site web) d'un télépro absent vers un autre. Tracé append-only dans l'historique CRM.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground block mb-1">
            <Users size={10} weight="bold" className="inline mr-1"/> Depuis (source)
          </label>
          <select
            value={fromId}
            onChange={(e) => setFromId(e.target.value)}
            className="w-full text-xs bg-background border border-border rounded-sm px-2 py-1.5"
            data-testid="handoff-from-select"
          >
            <option value="">— Choisir un télépro —</option>
            {telepros.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} — {t.first_name} {t.last_name}{t.absent_until ? " · absent" : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground block mb-1">
            <ArrowRight size={10} weight="bold" className="inline mr-1"/> Vers (cible)
          </label>
          <select
            value={toId}
            onChange={(e) => setToId(e.target.value)}
            className="w-full text-xs bg-background border border-border rounded-sm px-2 py-1.5"
            data-testid="handoff-to-select"
          >
            <option value="">— Choisir un télépro —</option>
            {telepros.filter((t) => t.id !== fromId).map((t) => (
              <option key={t.id} value={t.id}>{t.code} — {t.first_name} {t.last_name}</option>
            ))}
          </select>
        </div>
      </div>

      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Motif du transfert (ex : Absence 3 semaines) — optionnel mais recommandé"
        maxLength={280}
        className="w-full text-xs bg-background border border-border rounded-sm px-2 py-1.5 mb-3"
        data-testid="handoff-reason-input"
      />

      {fromId ? (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-muted-foreground">
              <Buildings size={11} weight="duotone" className="inline mr-1"/>
              <b className="text-foreground tabular-nums">{owned.count}</b> prospect(s) actuellement collé(s)
            </div>
            <button
              onClick={bulkTransfer}
              disabled={!toId || bulkRunning || owned.count === 0}
              className="bg-primary text-primary-foreground text-xs font-semibold rounded-sm px-3 py-1.5 hover:bg-[#0f3d29] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="handoff-bulk-btn"
            >
              {bulkRunning ? "Transfert…" : `Tout transférer (${owned.count})`}
            </button>
          </div>
          {owned.count === 0 ? (
            <div className="text-xs text-muted-foreground bg-muted/30 border border-dashed border-border rounded-sm p-3 text-center">
              Aucun prospect collé à ce télépro pour l'instant.
            </div>
          ) : (
            <ul className="max-h-56 overflow-y-auto space-y-1 border border-border rounded-sm p-1.5 bg-background" data-testid="handoff-owned-list">
              {owned.items.map((b) => (
                <li key={b.id} className="flex items-center gap-2 bg-card rounded-sm px-2 py-1.5 text-xs" data-testid={`handoff-item-${b.id}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                    b.quality_tier === "Excellent" ? "bg-primary" :
                    b.quality_tier === "Bon" ? "bg-emerald-500" :
                    b.quality_tier === "Moyen" ? "bg-amber-500" :
                    "bg-muted"
                  }`}/>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{b.name}</div>
                    <div className="text-[10px] text-muted-foreground">{b.city} · <span className="font-mono">{b.phone}</span></div>
                  </div>
                  <button
                    onClick={() => singleTransfer(b)}
                    disabled={!toId}
                    className="text-[10px] font-semibold text-primary hover:text-[#0f3d29] disabled:opacity-30 shrink-0"
                    data-testid={`handoff-transfer-${b.id}`}
                    title={toId ? "Transférer cette fiche uniquement" : "Choisis d'abord un télépro cible"}
                  >
                    Transférer →
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className="text-xs text-muted-foreground bg-muted/20 rounded-sm p-4 text-center">
          Sélectionne un télépro source pour voir ses prospects en cours.
        </div>
      )}
    </div>
  );
}
